export * from 'utils/bolt/dynamicData.js';
import Store from 'store/store';
import { isImage, isNativeVideo, isPDF, isYoutube, getYoutubeId } from 'utils/cms/mediaFiles';
import { commaSeparatedList } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';


function getHeroButton(data, button) {
	if (!button) return;
	let link = button.link;
	let label = button.label;
	if (link === '#tickets' && data.watch?.showShowtimes) {
		link = `${data.url}-showtimes`;
	}
	if (link === '#homeEnt' && data.watch?.showHomeEnt) {
		link = `${data.url}-homeEnt`;
	}
	if (button.autoTickets) {
		label = null;
		link = null;
		const today = formatDateTime(new Date(), 'yyyy-MM-dd');
		const date = data?.dates?.boxOffice || data?.dates?.release;
		if (date) {
			if (today < date) {
				label = 'Save to Calendar';
				link = `@calendarreminder::${date}`;
			} else if (data?.watch?.showShowtimes) {
				label = 'Get Tickets';
				link = `${data.url}-showtimes`;
			}
		}
	}
	return { label, link };
}


function getShowtimesDataOverrides(baseData, cmsData) {
	const boundingRegions = cmsData.watch?.boundingRegions?.split?.(',').map(r => r.trim()).filter(r => r) || [];
	const out = {
		...baseData,
		movieId: cmsData.watch.movieId,
		bg: { desktop: { asset: cmsData.watch.onesheet }, mobile: { asset: cmsData.watch.mobileOnesheet } },
		boundingRegions: Object.fromEntries(boundingRegions.map(r => [r, true])),
	};

	if (cmsData.watch?.movieId === '617dc132-b748-4f70-876f-cdf1d1ea9854' || cmsData.watch?.movieId === '4ad148cb-7030-4d27-bf0b-23b6544ac3db') {
		// avengers-doomsday
		const baseFormats = commaSeparatedList(baseData?.formatsAllowed);
		out.formatsAllowed = baseFormats.concat(baseFormats.map(f => `${f}-IV`)).join(',');
		out.rules ||= {};
		out.rules.infinityVision = { name: 'thundr_infinity_vision_4DX_screenX_hub' };
		out.rules.dedupe = { name: 'dedupe_keep_longest_format', parameters: 'merge-links' };
	}

	const isMultiMovie = cmsData.watch?.movieId?.split(',').length > 1;

	if (isMultiMovie) {
		out.screeningCategories = {
			...baseData.screeningCategories,
			exclusions: {
				...baseData.screeningCategories?.exclusions,
				movieId: false
			}
		};
	} 

	return out;
}

export function transformPageData(data) {
	const appData = Store.get().appData;

	const youtubeMatch = data.hero?.youtube?.match(/[&?]v=([^&#]+)|youtu\.be\/([^?&#]+)|^([a-z0-9_-]+)$/i);
	const youtubeId = youtubeMatch?.[1] || youtubeMatch?.[2] || youtubeMatch?.[3];

	const homeEntData = data.watch.homeEntData;
	const finalData = {};
	for (let merchant of homeEntData) {
		if (!finalData[merchant.format]) {
			finalData[merchant.format] = {};
		}
		finalData[merchant.format][merchant.name] = {
			merchantName: merchant.name,
			[merchant.availability]: [
				{
					link: merchant.link
				}
			]
		};
	}

	const landingPage = data.landingPage || 'about';
	const filmPageSubPath = landingPage === 'about' ? '/' : '/about-the-film/';
	const showtimesPageSubPath = landingPage === 'showtimes' ? '/' : '/tickets/';

	if (!data.enabled) return [{
		base: 'bolt',
		id: data.url,
		data: {
			disabledPage: true
		},
		type: 'filmPage',
		path: '/films/' + data.url + filmPageSubPath,
		customPageComponent: 'films'
	}];

	return [
		{
			base: 'bolt',
			id: data.url,
			data: [
				{
					id: 'filmHero',
					component: 'heroSpot',
					data: {
						background: {
							alt: data.hero?.alt,
							desktop: data.hero?.img,
							mobile: data.hero?.mobileImg
						},
						buttons: data?.hero?.buttons?.map(button => getHeroButton(data, button))?.filter(b => b?.label),
						description: data.hero?.description,
						hasGradient: true,
						title: data.hero?.title,
						trailer: !youtubeId ? undefined : {
							trailer: {
								name: '',
								thumbSrc: `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`,
								youtubeId: youtubeId
							}
						}
					},
				},
				data.synopsis?.content && {
					id: 'filmSynopsis',
					component: 'synopsis',
					data: {
						hash: 'synopsis',
						content: data.synopsis?.content,
						credits: data.synopsis?.credits?.map(e => ({ heading: e?.title, names: e?.content?.replace(/(^[^ ]|.[^ ]|[^ ].)\n/gm, '$1  \n') })).filter(e => e.names),
						onesheet: data.synopsis?.onesheet,
						onesheetAltText: data.synopsis?.onesheetAltText,
					},
				},
				data.gallery?.length && {
					id: 'filmGallery',
					component: 'gallery',
					data: {
						hash: 'gallery',
						contentCarousel: data.gallery.map((e) => {
							if (!e.file) return;
							if (isImage(e.file)) {
								return { desktopSrc: e.file, alt: e.alt, annotation: e.annotation };
							}
							if (isNativeVideo(e.file)) {
								return { videoSrc: e.file, annotation: e.annotation };
							}
							if (isPDF(e.file)) {
								return { pdfSrc: e.file, annotation: e.annotation, desktopSrc: e.pdfPreview };
							}
							if (isYoutube(e.file)) {
								const ytId = getYoutubeId(e.file);
								if (ytId) {
									return { youtubeId: ytId, annotation: e.annotation };
								}
							}
						}).filter(e => e),
						downloadAll: false,
						downloadSingle: true,
						hideArrowsMobile: true
					},
				},
			].filter(e => e),
			type: 'filmPage',
			path: '/films/' + data.url + filmPageSubPath,
			customPageComponent: 'films'
		},
		data.watch?.showShowtimes && {
			id: `${data.url}-showtimes`,
			type: 'showtimes',
			path: `/films/${data.url}${showtimesPageSubPath}`,
			data: getShowtimesDataOverrides(appData?.pages?.boltShowtimes?.data, data),
		}
		// data.watch?.showHomeEnt && {
		// 	id: `${data.url}-homeEnt`,
		// 	type: 'homeEnt',
		// 	path: `/films/${data.url}/watch-at-home/`,
		// 	data: {
		// 		movieId: data.watch.movieId,
		// 		regions: 'gb',
		// 		bg: { desktop: { asset: data.watch.onesheet }, mobile: { asset: data.watch.mobileOnesheet } },
		// 		addProducts: finalData,
		// 		overrideMerchantDetails: data.watch.homeEntData?.reduce?.((data, e) => {
		// 			if (!e?.name) {
		// 				return data;
		// 			}
		// 			data[e.name] = { name: e.name, logo: e.logo };
		// 			return data;
		// 		}, {}),
		// 	},
		// }
	].filter(e => e);
}